<!--
  PhotoField — capture photos inline and attach them to a record.

  Spec, "Screens": "Photos — capture inline from the camera, attached to a
  section or row." A photo of the batch computer screen belongs to the
  calibration trial it evidences (Appendix C step 5), not to the pour in
  general, so this control is placed *next to the row it documents* and is told
  which row that is.

  Two ways in, one path through: the camera control (`capture="environment"`)
  goes straight to the phone's camera; the gallery control is the identical
  file input minus that attribute, which is what hands back the OS picker
  instead. Both `onchange` handlers call the same `onFiles`, so a gallery pick
  — however large, however it was encoded — downscales and queues exactly like
  a fresh capture (see downscalePhoto's header in ../photo.ts: it re-encodes
  every accepted image regardless of its original size, so a multi-megabyte
  gallery PNG is no different from a 3 MB camera JPEG once decoded).

  Spec, "Error handling": "Photo upload failure: the record saves regardless;
  the photo is queued and retried, and shows as pending rather than silently
  vanishing." Everything here is therefore optimistic — the thumbnail appears
  the instant the capture is downscaled, carrying a pending badge until the
  queue in ../photo.ts lands it. Nothing about taking a photo blocks the form,
  and no failure opens a dialog ("No modal dialogs that can trap a user
  mid-pour").

  "Never change recorded history": a photo actually TAKEN after the pour it
  belongs to has already closed is new evidence, not a rewrite — the row is
  never hidden or refused, but `closedAt` lets this field mark it as filed
  late so the record shows the truth about when it arrived rather than
  looking like it was always there. The judgment is ../calc's
  `photoAddedAfterClose(photo, closedAt)`, which compares `taken_at` (set on
  the device the instant the shutter fires), NOT `created_at` (set by the
  server at attach time) — a photo can be taken mid-pour and only attach
  long after close (a bad-signal retry, or the whole backlog stranded while
  R2 was off), and none of that delay is evidence of when it was taken.

  Gloves, daylight, one hand: both capture controls are full-width 3rem
  targets, and viewing a photo full size opens a new tab rather than a
  lightbox.
-->
<script lang="ts">
  import { currentRole } from '../api';
  import { photoAddedAfterClose } from '../calc';
  import {
    DuplicatePhotoError,
    capturePhoto,
    discardPhoto,
    pendingPhotos,
    photoContentUrl,
    retryPhoto,
    type PendingPhoto
  } from '../photo';
  import type { Photo, PhotoSection, Timestamp } from '../types';

  interface Props {
    /** The pour these photos belong to. */
    pourId: number;
    /** Which part of the record. null = the pour in general. */
    section?: PhotoSection | null;
    /** The id of the row within `section` the photo evidences. */
    rowId?: number | null;
    label?: string;
    /** Wording on the camera control — takes a fresh photo right now. */
    actionLabel?: string;
    /**
     * Wording on the gallery control — picks an existing image instead of
     * taking a new one. A generic default (works unchanged at every call
     * site); pass a specific one only where the extra context earns the
     * space.
     */
    galleryLabel?: string;
    /**
     * Photos already attached. Pass the whole pour's list; this control shows
     * only the ones matching its own section + row.
     */
    photos?: Photo[];
    /** Stamped on every photo taken here, e.g. 'Batch computer - trial 2'. */
    caption?: string | null;
    /**
     * Read-only sessions (client role) get the gallery and no camera.
     * Defaults from the session, so a screen cannot forget to pass it — the
     * server refuses the write either way, but a client must not see a control
     * that cannot work.
     */
    readOnly?: boolean;
    /**
     * The pour's `closed_at`, so an attached photo can be marked as added
     * after close rather than silently looking original. null (the default)
     * for an open pour, or wherever the caller has not loaded the pour.
     */
    closedAt?: Timestamp | null;
    /** Small helper line, e.g. 'Photograph the batch computer screen'. */
    hint?: string;
    id?: string;
  }

  let {
    pourId,
    section = null,
    rowId = null,
    label = 'Photos',
    actionLabel = 'Add photo',
    galleryLabel = '🖼️ Choose from gallery',
    photos = [],
    caption = null,
    readOnly = currentRole() !== 'crew',
    closedAt = null,
    hint = '',
    id = `photo-${Math.random().toString(36).slice(2, 9)}`
  }: Props = $props();

  /* ------------------------------------------------------------------ state */

  let busy = $state(false);
  let problem = $state<string | null>(null);
  /**
   * "Duplicate photos": set instead of `problem` when `capturePhoto` refuses
   * an add because the exact same file is already queued or attached to this
   * pour. Kept apart from `problem` on purpose — a duplicate is a normal
   * thing a person does with a thumb on a small screen, not a failure, and it
   * should read that way (see the `.notice` style below) rather than in the
   * same red text a broken upload gets.
   */
  let notice = $state<string | null>(null);

  /**
   * Fix-round 2, M5: Discard on a pending (unsent) photo hard-deletes its
   * IndexedDB record, Blob included — the ONLY copy that exists, since the
   * upload never completed. This app forbids `confirm()` and has one
   * standing pattern for "this button destroys something": an inline
   * Confirm/Keep pair that self-cancels, the same one
   * ../../routes/sections/ChuteTests.svelte uses twelve lines below its own
   * PhotoField to guard a RECOVERABLE row delete. An irreplaceable photo
   * deserves at least that much friction.
   *
   * Keyed by photo id (not a single boolean) so confirming one pending
   * photo's discard does not also arm every other one in the same field.
   */
  let confirmingDiscard = $state<string | null>(null);
  let discardConfirmTimer: ReturnType<typeof setTimeout> | null = null;

  const attached = $derived(
    photos.filter(
      (photo) => (photo.section ?? null) === section && (photo.row_id ?? null) === rowId
    )
  );

  // One subscription to the global queue, filtered locally: cheaper than a
  // derived store per field, and there is one of these next to every row.
  const pending = $derived(
    $pendingPhotos.filter(
      (photo) =>
        photo.pour_id === pourId &&
        (photo.section ?? null) === section &&
        (photo.row_id ?? null) === rowId
    )
  );

  const total = $derived(attached.length + pending.length);

  /* ----------------------------------------------------------------- capture */

  async function onFiles(event: Event) {
    const target = event.currentTarget as HTMLInputElement;
    const files = Array.from(target.files ?? []);
    // Clear immediately so picking the same file twice still fires a change.
    target.value = '';
    if (files.length === 0) return;

    busy = true;
    problem = null;
    notice = null;
    try {
      for (const file of files) {
        try {
          // Resolves as soon as the image is downscaled and on the device.
          // The upload happens afterwards, on the queue's own schedule.
          // capturePhoto checks for a duplicate BEFORE any of that, so a
          // repeat pick never reaches the queue at all — see photo.ts's
          // guardAgainstDuplicatePhoto.
          await capturePhoto(pourId, file, { section, rowId, caption });
        } catch (cause) {
          if (cause instanceof DuplicatePhotoError) {
            notice = cause.message;
          } else {
            problem = cause instanceof Error ? cause.message : 'That photo could not be read.';
          }
        }
      }
    } finally {
      busy = false;
    }
  }

  function statusText(photo: PendingPhoto): string {
    if (photo.status === 'uploading') return 'Uploading';
    if (!photo.retryable) return photo.error ?? 'Not uploaded';
    if (photo.attempts === 0) return 'Waiting to upload';
    return `Pending - retry ${photo.attempts}`;
  }

  /* ------------------------------------------------------------ discard, confirmed */

  /** Arm the Confirm/Keep pair for one pending photo. Self-cancels at 4 s. */
  function askDiscard(id: string): void {
    if (discardConfirmTimer) clearTimeout(discardConfirmTimer);
    confirmingDiscard = id;
    // Inline, self-cancelling. No dialog can trap a user mid-pour.
    discardConfirmTimer = setTimeout(() => (confirmingDiscard = null), 4000);
  }

  /** The only path that actually calls discardPhoto — after confirmation. */
  function confirmDiscard(id: string): void {
    if (discardConfirmTimer) clearTimeout(discardConfirmTimer);
    confirmingDiscard = null;
    discardPhoto(id);
  }

  function keepDiscard(): void {
    if (discardConfirmTimer) clearTimeout(discardConfirmTimer);
    confirmingDiscard = null;
  }

  /**
   * True when this photo is evidence filed after the pour closed. Thin
   * wrapper around ../calc's `photoAddedAfterClose` (the shared predicate
   * the exports use too) so the template does not have to pass `closedAt`
   * at every call site.
   */
  function addedAfterClose(photo: Photo): boolean {
    return photoAddedAfterClose(photo, closedAt);
  }
</script>

<div class="pf">
  <div class="head">
    <span class="label" {id}>{label}</span>
    {#if total > 0}<span class="count">{total}</span>{/if}
  </div>

  {#if total > 0}
    <ul class="grid" aria-labelledby={id}>
      {#each attached as photo (photo.id)}
        <li class="tile">
          <a href={photoContentUrl(photo.id)} target="_blank" rel="noopener noreferrer">
            <img
              src={photoContentUrl(photo.id)}
              alt={photo.caption ?? 'Attached photo'}
              loading="lazy"
              decoding="async"
            />
          </a>
          {#if photo.caption}<p class="cap">{photo.caption}</p>{/if}
          {#if addedAfterClose(photo)}
            <p class="after-close">Added after close</p>
          {/if}
        </li>
      {/each}

      {#each pending as photo (photo.id)}
        <li class="tile pending" class:stuck={!photo.retryable}>
          {#if photo.preview_url}
            <img src={photo.preview_url} alt="Waiting to upload" decoding="async" />
          {:else}
            <div class="noprev" aria-hidden="true"></div>
          {/if}
          <p class="badge" class:bad={!photo.retryable}>{statusText(photo)}</p>
          {#if !readOnly && photo.status !== 'uploading'}
            <div class="acts">
              <button type="button" onclick={() => retryPhoto(photo.id)}>Retry</button>
              {#if confirmingDiscard === photo.id}
                <button type="button" class="danger" onclick={() => confirmDiscard(photo.id)}>
                  Confirm discard
                </button>
                <button type="button" onclick={keepDiscard}>Keep</button>
              {:else}
                <button type="button" class="danger" onclick={() => askDiscard(photo.id)}>
                  Discard
                </button>
              {/if}
            </div>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}

  {#if !readOnly}
    <div class="captures">
      <label class="capture" class:busy>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          disabled={busy}
          onchange={onFiles}
        />
        <span>{busy ? 'Preparing...' : actionLabel}</span>
      </label>
      <!-- Identical input, minus `capture`: that one attribute is the whole
           difference between "open the camera" and "open the photo picker". -->
      <label class="capture" class:busy>
        <input type="file" accept="image/*" multiple disabled={busy} onchange={onFiles} />
        <span>{busy ? 'Preparing...' : galleryLabel}</span>
      </label>
    </div>
  {/if}

  {#if problem}
    <p class="problem" role="status">{problem}</p>
  {/if}

  {#if notice}
    <p class="notice" role="status">{notice}</p>
  {/if}

  {#if hint}<p class="hint">{hint}</p>{/if}
</div>

<style>
  .pf {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 0;
  }

  .head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .label {
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.2;
    color: var(--fg-muted, #3f4750);
    letter-spacing: 0.01em;
  }

  .count {
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1;
    padding: 0.1875rem 0.375rem;
    border-radius: 0.75rem;
    color: var(--fg, #10151b);
    background: var(--surface-muted, #eef1f4);
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(6.5rem, 1fr));
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .tile {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    border: 2px solid var(--border, #9aa3ad);
    border-radius: 0.5rem;
    background: var(--surface, #fff);
    overflow: hidden;
  }

  .tile a {
    display: block;
    line-height: 0;
  }

  .tile img,
  .noprev {
    display: block;
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    background: var(--surface-muted, #eef1f4);
  }

  /* Pending photos are visibly not-yet-filed, never merely absent. */
  .pending {
    border-color: var(--warn, #a35a00);
  }
  .pending img {
    opacity: 0.72;
  }
  .stuck {
    border-color: var(--bad, #b3261e);
  }

  .badge {
    margin: 0;
    padding: 0 0.375rem;
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 1.3;
    color: var(--warn, #a35a00);
    overflow-wrap: anywhere;
  }
  .badge.bad {
    color: var(--bad, #b3261e);
  }

  .cap {
    margin: 0;
    padding: 0 0.375rem 0.25rem;
    font-size: 0.6875rem;
    line-height: 1.3;
    color: var(--fg-muted, #3f4750);
    overflow-wrap: anywhere;
  }

  /* "Never change recorded history": evidence added after close is shown,
     never hidden — but marked, so the record does not imply it was always
     there. Informational, not a warning, so it gets the accent colour rather
     than the pending badge's warn/bad ones. */
  .after-close {
    margin: 0;
    padding: 0 0.375rem 0.25rem;
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 1.3;
    color: var(--accent, #0b63c5);
  }

  .acts {
    display: flex;
    gap: 0.25rem;
    padding: 0 0.25rem 0.25rem;
  }

  .acts button {
    flex: 1 1 auto;
    min-height: 2rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--fg, #10151b);
    background: var(--surface-muted, #eef1f4);
    border: 1px solid var(--border, #9aa3ad);
    border-radius: 0.375rem;
  }

  .acts .danger {
    color: var(--bad, #b3261e);
  }

  /* Camera and gallery side by side where there is room; wraps to stacked
     full-width rows on a narrow phone rather than squeezing both labels. */
  .captures {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  /* The capture control is the label; the input itself is never seen. */
  .capture {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1 1 12rem;
    min-height: 3rem;
    padding: 0.75rem 1rem;
    font-size: 1rem;
    font-weight: 700;
    color: var(--fg, #10151b);
    background: var(--surface-muted, #eef1f4);
    border: 2px dashed var(--border, #9aa3ad);
    border-radius: 0.5rem;
    cursor: pointer;
  }

  .capture:focus-within {
    border-color: var(--accent, #0b63c5);
    box-shadow: 0 0 0 3px var(--accent-ring, rgba(11, 99, 197, 0.28));
  }

  .capture.busy {
    opacity: 0.7;
  }

  .capture input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  .problem {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.25;
    color: var(--bad, #b3261e);
  }

  /* "Duplicate photos": deliberately NOT .problem's red — a duplicate is the
     app noticing, not the app failing. Same accent used for "Added after
     close" below, the app's other informational-not-alarming marker. */
  .notice {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.25;
    color: var(--accent, #0b63c5);
  }

  .hint {
    margin: 0;
    font-size: 0.75rem;
    line-height: 1.25;
    color: var(--fg-muted, #3f4750);
  }
</style>
